import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { useNavigate, Link } from "react-router-dom";
import { Row, Col, Card, Button, Table, Form, Badge, Spinner, Pagination } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../layouts/PageLayout";

const COUNTRY_LIST = [
    "United States", "United Kingdom", "Canada", "Australia", "Germany", "France",
    "United Arab Emirates", "Saudi Arabia", "Pakistan", "India", "Afghanistan",
    "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Austria",
    "Azerbaijan", "Bahrain", "Bangladesh", "Belgium", "Brazil", "Bulgaria",
    "China", "Colombia", "Croatia", "Cyprus", "Czech Republic", "Denmark",
    "Egypt", "Estonia", "Finland", "Georgia", "Greece", "Hong Kong", "Hungary",
    "Iceland", "Indonesia", "Iraq", "Ireland", "Italy", "Japan", "Jordan",
    "Kuwait", "Latvia", "Lebanon", "Lithuania", "Luxembourg", "Malaysia",
    "Mexico", "Morocco", "Netherlands", "New Zealand", "Nigeria", "Norway",
    "Oman", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia",
    "Singapore", "South Africa", "South Korea", "Spain", "Sri Lanka", "Sweden",
    "Switzerland", "Thailand", "Turkey", "Ukraine", "Vietnam"
];

export default function MyOrdersListPage() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters state
    const [filters, setFilters] = useState({
        search: "",
        delivery_status: "",
        from_date: "",
        to_date: "",
        buyer_name: "",
        buyer_email: "",
        buyer_phone: "",
        buyer_country: "",
    });

    const [pagination, setPagination] = useState({ currentPage: 1, lastPage: 1, total: 0 });

    const fetchOrders = useCallback(async (page = 1, currentFilters = filters) => {
        setLoading(true);
        try {
            const params = { page };
            Object.keys(currentFilters).forEach((key) => {
                if (currentFilters[key]) params[key] = currentFilters[key];
            });

            const response = await axios.get(`${process.env.REACT_APP_API_URL}/market-placer/my-order`, {
                params,
                headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
            });

            if (response.data?.success) {
                setOrders(response.data.data || []);
                setPagination({
                    currentPage: response.data.meta?.current_page || page,
                    lastPage: response.data.meta?.last_page || 1,
                    total: response.data.meta?.total || 0,
                });
            }
        } catch (err) {
            console.error("Error fetching orders:", err);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        const timer = setTimeout(() => fetchOrders(1, filters), 400);
        return () => clearTimeout(timer);
    }, [filters, fetchOrders]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const handleResetFilters = () => {
        const resetObj = {
            search: "",
            delivery_status: "",
            from_date: "",
            to_date: "",
            buyer_name: "",
            buyer_email: "",
            buyer_phone: "",
            buyer_country: "",
        };
        setFilters(resetObj);
        fetchOrders(1, resetObj);
    };

    // Responsive Smart Pagination helper
    const renderPaginationItems = () => {
        const { currentPage, lastPage } = pagination;
        const items = [];
        const delta = 1;

        let left = currentPage - delta;
        let right = currentPage + delta + 1;

        items.push(
            <Pagination.Prev
                key="prev"
                disabled={currentPage === 1}
                onClick={() => fetchOrders(currentPage - 1)}
            />
        );

        items.push(
            <Pagination.Item
                key={1}
                active={1 === currentPage}
                onClick={() => fetchOrders(1)}
            >
                1
            </Pagination.Item>
        );

        if (left > 2) {
            items.push(<Pagination.Ellipsis key="ellipsis-1" disabled />);
        }

        for (let i = Math.max(2, left); i <= Math.min(lastPage - 1, right); i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => fetchOrders(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (right < lastPage - 1) {
            items.push(<Pagination.Ellipsis key="ellipsis-2" disabled />);
        }

        if (lastPage > 1) {
            items.push(
                <Pagination.Item
                    key={lastPage}
                    active={lastPage === currentPage}
                    onClick={() => fetchOrders(lastPage)}
                >
                    {lastPage}
                </Pagination.Item>
            );
        }

        items.push(
            <Pagination.Next
                key="next"
                disabled={currentPage === lastPage}
                onClick={() => fetchOrders(currentPage + 1)}
            />
        );

        return items;
    };

    const isFilterApplied = Object.values(filters).some((val) => val !== "");

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <Card className="mc-card mb-4 border-0 shadow-sm">
                        <Card.Body className="mc-breadcrumb d-flex justify-content-between align-items-center py-3">
                            <div>
                                <h3 className="mc-breadcrumb-title mb-1 fw-bold">{t("My Orders")}</h3>
                                <ul className="mc-breadcrumb-list d-flex gap-2 p-0 m-0 list-unstyled small text-muted">
                                    <li><Link to="#" className="text-decoration-none text-muted">{t("Home")}</Link></li>
                                    <li>/</li>
                                    <li className="text-primary fw-semibold">{t("My Orders")}</li>
                                </ul>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white py-3">
                            <Row className="g-3">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold text-muted">{t("Search")}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="search"
                                            placeholder={t("Order #, code, title...")}
                                            value={filters.search}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold text-muted">{t("Delivery Status")}</Form.Label>
                                        <Form.Select
                                            name="delivery_status"
                                            value={filters.delivery_status}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        >
                                            <option value="">{t("All Statuses")}</option>
                                            <option value="Pending">{t("Pending")}</option>
                                            <option value="Shipped">{t("Shipped")}</option>
                                            <option value="Delivered">{t("Delivered")}</option>
                                            <option value="Returned">{t("Returned")}</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold text-muted">{t("Buyer Name")}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="buyer_name"
                                            placeholder={t("Buyer name")}
                                            value={filters.buyer_name}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold text-muted">{t("Buyer Email")}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="buyer_email"
                                            placeholder={t("Buyer email")}
                                            value={filters.buyer_email}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold text-muted">{t("Buyer Phone")}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="buyer_phone"
                                            placeholder={t("Buyer phone")}
                                            value={filters.buyer_phone}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold text-muted">{t("Buyer Country")}</Form.Label>
                                        <Form.Select
                                            name="buyer_country"
                                            value={filters.buyer_country}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        >
                                            <option value="">{t("All Countries")}</option>
                                            {COUNTRY_LIST.map((c) => (
                                                <option key={c} value={c}>{c}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold text-muted">{t("From Date")}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="from_date"
                                            value={filters.from_date}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold text-muted">{t("To Date")}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="to_date"
                                            value={filters.to_date}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2} className="d-flex align-items-end">
                                    {isFilterApplied && (
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            className="w-100"
                                            onClick={handleResetFilters}
                                        >
                                            {t("Clear Filters")}
                                        </Button>
                                    )}
                                </Col>
                            </Row>
                        </Card.Header>

                        <Card.Body className="p-0">
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                </div>
                            ) : (
                                <Table responsive hover className="align-middle mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>{t("Order #")}</th>
                                            <th>{t("Products")}</th>
                                            <th>{t("Grand Total")}</th>
                                            <th>{t("Delivery Status")}</th>
                                            <th>{t("Buyer Name")}</th>
                                            <th>{t("Ordered At")}</th>
                                            <th className="text-end">{t("Action")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {orders.length > 0 ? (
                                            orders.map((order) => (
                                                <tr key={order.id}>
                                                    <td className="fw-bold">{order.order_number}</td>
                                                    <td>
                                                        {order.products?.map((p, idx) => (
                                                            <div key={idx} className="small">
                                                                <span className="fw-semibold">{p.title}</span> ({p.product_code || "-"})
                                                            </div>
                                                        ))}
                                                    </td>
                                                    <td className="fw-bold text-success">${order.grand_total}</td>
                                                    <td>
                                                        <Badge bg={order.delivery_status === "Delivered" ? "success" : "info"}>
                                                            {order.delivery_status || "-"}
                                                        </Badge>
                                                    </td>
                                                    <td className="small">{order.buyer_details?.name || "-"}</td>
                                                    <td className="small">{order.ordered_at ? new Date(order.ordered_at).toLocaleDateString() : "-"}</td>
                                                    <td className="text-end">
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            onClick={() => navigate(`/market-placer/my-order/${order.id}`, { state: { order } })}
                                                        >
                                                            {t("View")}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={7} className="text-center py-4 text-muted">
                                                    {t("No orders found.")}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>

                        {/* Responsive Smart Pagination */}
                        {pagination.lastPage > 1 && (
                            <Card.Footer className="bg-white d-flex flex-wrap justify-content-between align-items-center py-3">
                                <span className="small text-muted mb-2 mb-md-0">
                                    {t("Showing page")} {pagination.currentPage} {t("of")} {pagination.lastPage} ({pagination.total} {t("total orders")})
                                </span>
                                <Pagination size="sm" className="mb-0 flex-wrap">
                                    {renderPaginationItems()}
                                </Pagination>
                            </Card.Footer>
                        )}
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}